import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Upload, AlertTriangle, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

const RestoreBackupModal = ({ open, onOpenChange, onRestore }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type - accept MongoDB native backup (.tar.gz)
      if (!file.name.endsWith('.tar.gz') && !file.name.endsWith('.json')) {
        setResult({
          success: false,
          message: 'Le fichier doit être au format .tar.gz (MongoDB natif) ou .json (ancien format)'
        });
        return;
      }
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) return;

    setRestoring(true);
    setResult(null);

    try {
      const response = await onRestore(selectedFile);
      setResult(response);
      
      if (response.success) {
        // Close modal after 2 seconds on success
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (error) {
      setResult({
        success: false,
        message: error.message || 'Erreur lors de la restauration'
      });
    } finally {
      setRestoring(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setResult(null);
    setRestoring(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Restaurer depuis un Backup
          </DialogTitle>
          <DialogDescription>
            Importez un fichier de backup pour restaurer toutes les données
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning Alert */}
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Attention :</strong> Cette action est irréversible. Toutes les données actuelles
              (clubs, membres, PV, propositions) seront <strong>supprimées et remplacées</strong> par
              celles du backup. Le compte Administrateur sera préservé.
            </AlertDescription>
          </Alert>

          {/* File Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Fichier de backup</label>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".tar.gz,.json"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={restoring}
                className="w-full"
              >
                <Upload className="h-4 w-4 mr-2" />
                {selectedFile ? selectedFile.name : 'Sélectionner un fichier .tar.gz ou .json'}
              </Button>
            </div>
            {selectedFile && (
              <p className="text-sm text-muted-foreground">
                Taille: {(selectedFile.size / 1024).toFixed(2)} KB
              </p>
            )}
          </div>

          {/* Result Display */}
          {result && (
            <Alert variant={result.success ? "default" : "destructive"}>
              {result.success ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              <AlertDescription>
                {result.success ? (
                  <div className="space-y-3">
                    <p className="font-semibold mb-2">✅ Restauration réussie !</p>
                    {result.details && (
                      <ul className="text-sm space-y-1">
                        <li>• {result.details.clubs_restored} clubs restaurés</li>
                        <li>• {result.details.members_restored} membres restaurés</li>
                        <li>• {result.details.pvs_restored} PV restaurés</li>
                        <li>• {result.details.proposition_sessions_restored} sessions de propositions</li>
                        <li>• {result.details.propositions_restored} propositions</li>
                      </ul>
                    )}
                    {result.details?.temporary_passwords && result.details.temporary_passwords.length > 0 && (
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                        <p className="font-semibold text-yellow-800 mb-2">
                          ⚠️ Mots de passe temporaires générés
                        </p>
                        <p className="text-sm text-yellow-700 mb-2">
                          Les clubs suivants ont reçu des mots de passe temporaires (les originaux ont été exclus pour sécurité) :
                        </p>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {result.details.temporary_passwords.map((club, idx) => (
                            <div key={idx} className="text-xs font-mono bg-white p-2 rounded border">
                              <span className="font-semibold">{club.username}</span>: {club.temp_password}
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-yellow-600 mt-2">
                          💡 Notez ces mots de passe et communiquez-les aux clubs concernés pour qu'ils les changent.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p>{result.message}</p>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={restoring}
          >
            Annuler
          </Button>
          <Button
            onClick={handleRestore}
            disabled={!selectedFile || restoring || (result && result.success)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {restoring ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Restauration en cours...
              </>
            ) : (
              'Confirmer la Restauration'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RestoreBackupModal;
